/**
 * UI store — IMPLEMENTATION.md §14 (Tweaks panel) drives most of this.
 *  - theme:    light | dark
 *  - brand:    vin | vspn
 *  - density:  comfortable | compact
 *  - slideRailMode: focus | filter   (Tweaks panel radio only; the editor SlideRail uses its own local ref)
 *  - focusedSlideId: id | null       (F1/F2 closures from §15)
 *  - aiFlagOverlays: boolean         (editor flag-overlay toggle)
 *  - statusBarVisible: boolean       (editor sticky-bottom debug bar)
 *  - classifyBackend / classifyModel: DEAD. See the note on ClassifyBackend below.
 */
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export type Theme = 'light' | 'dark';
export type Brand = 'vin' | 'vspn';
export type Density = 'comfortable' | 'compact';
/**
 * Read and written only by the Tweaks panel "Slide-rail mode" radio
 * (App.vue:162). That control has NO effect on the editor. The live mode is the
 * local ref in EditorView.vue, persisted under `mic_slide_click_mode`, and its
 * type is SlideRailMode in @/utils/segmentView (four values, not two). Do not
 * wire this up or export a mode type from here.
 */
export type SlideRailMode = 'focus' | 'filter';
/**
 * DEAD — and NOT the vocabulary the classification backend actually uses.
 *
 * Nothing reads classifyBackend: it is written to localStorage under
 * `rounds_ui_v1` and never consulted. The live setting is org_settings
 * .classify_backend, owned by SectionDiscrepancy.vue's own ref, and its two
 * values are `gemini-dev` and `vertex` WITH A HYPHEN (see CLASSIFY_BACKENDS in
 * fixtures/settings.ts). app/tasks/classify_task.py routes on exactly that:
 * `use_vertex = (backend == "vertex")`.
 *
 * Do not wire this type up to that control to make the two agree. Renaming the
 * option value to `vertex_ai` would send classifyModelsFor down the
 * eleven-model Developer-API branch while use_vertex went False — the UI would
 * say Vertex while the worker called the Developer API. Both halves wrong.
 * tests/test_model_list.py::test_vertex_sentinel_is_the_same_string_in_both_languages
 * fails if anyone tries.
 */
export type ClassifyBackend = 'gemini_dev' | 'vertex_ai';

const STORAGE_KEY = 'rounds_ui_v1';

interface Persisted {
  theme: Theme;
  brand: Brand;
  density: Density;
  slideRailMode: SlideRailMode;
  aiFlagOverlays: boolean;
  statusBarVisible: boolean;
  classifyBackend: ClassifyBackend;
  classifyModel: string;
  fontScale: number;
}

// Transcript font-size multiplier driven by the A+/A- topbar buttons. Scoped
// to transcript text via the --transcript-font-scale CSS var (see app.css
// .segment__text) so the rest of the chrome layout is unaffected. Clamped so
// the transcript stays readable and the layout can't break.
const FONT_SCALE_MIN = 0.85;
const FONT_SCALE_MAX = 1.4;

function loadPersisted(): Partial<Persisted> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as Partial<Persisted> : {};
  } catch { return {}; }
}

export const useUiStore = defineStore('ui', () => {
  const persisted = loadPersisted();

  const theme = ref<Theme>(persisted.theme ?? 'light');
  const brand = ref<Brand>(persisted.brand ?? 'vin');
  const density = ref<Density>(persisted.density ?? 'comfortable');
  const slideRailMode = ref<SlideRailMode>(persisted.slideRailMode ?? 'focus');
  const aiFlagOverlays = ref<boolean>(persisted.aiFlagOverlays ?? true);
  const statusBarVisible = ref<boolean>(persisted.statusBarVisible ?? false);
  const classifyBackend = ref<ClassifyBackend>(persisted.classifyBackend ?? 'gemini_dev');
  const classifyModel = ref<string>(persisted.classifyModel ?? 'gemini-2.5-flash-lite');
  const fontScale = ref<number>(
    Math.max(FONT_SCALE_MIN, Math.min(FONT_SCALE_MAX, persisted.fontScale ?? 1)),
  );

  const focusedSlideId = ref<string | null>(null);  // session-scoped; not persisted

  function setTheme(value: Theme) {
    theme.value = value;
    document.documentElement.dataset.theme = value;
  }
  function setBrand(value: Brand) {
    brand.value = value;
    document.documentElement.dataset.brand = value;
  }
  function setDensity(value: Density) {
    density.value = value;
    document.documentElement.dataset.density = value;
  }
  function clearFocus() { focusedSlideId.value = null; }   // F1 / F2 closure helper

  function applyFontScale() {
    document.documentElement.style.setProperty('--transcript-font-scale', String(fontScale.value));
  }
  function setFontScale(value: number) {
    const clamped = Math.max(FONT_SCALE_MIN, Math.min(FONT_SCALE_MAX, Math.round(value * 100) / 100));
    fontScale.value = clamped;
    applyFontScale();
  }

  // Initial DOM sync
  setTheme(theme.value);
  setBrand(brand.value);
  setDensity(density.value);
  applyFontScale();

  // Persist on any change to durable fields
  watch(
    [theme, brand, density, slideRailMode, aiFlagOverlays, statusBarVisible, classifyBackend, classifyModel, fontScale],
    () => {
      const snapshot: Persisted = {
        theme: theme.value,
        brand: brand.value,
        density: density.value,
        slideRailMode: slideRailMode.value,
        aiFlagOverlays: aiFlagOverlays.value,
        statusBarVisible: statusBarVisible.value,
        classifyBackend: classifyBackend.value,
        classifyModel: classifyModel.value,
        fontScale: fontScale.value,
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot)); } catch { /* quota or private mode */ }
    },
    { deep: false },
  );

  return {
    theme, brand, density, slideRailMode, aiFlagOverlays, statusBarVisible,
    classifyBackend, classifyModel, focusedSlideId, fontScale,
    setTheme, setBrand, setDensity, clearFocus, setFontScale,
  };
});
